import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { API_URL } from '../../constant';
import './CaptainTCCardAgent.css';
import fivePoundCardImg from '../../images/captain-tc/5PoundCard.jpg';
import wallet2 from "../../images/shapes/Wallet03.1 1.png";
import oldbank from "../../images/shapes/bank-pound-icon2 4.png";
import promoTopCoinIcon from "../../images/shapes/tc-units-wallet 2.png";
import promoCashIcon from "../../images/shapes/tc-cash-wallet 1.png";

function CaptainTCCardAgent() {
    const outletContext = useOutletContext() || {};
    const { userData } = outletContext;

    const [loading, setLoading] = useState(true);
    const [cards, setCards] = useState([]);

    // Sales history
    const [salesHistory, setSalesHistory] = useState([]);
    const [salesLoading, setSalesLoading] = useState(false);

    // Agent TopCoin Wallet
    const [agentWalletBalance, setAgentWalletBalance] = useState(0);

    // Promo wallet balances
    const [extraCardBalance, setExtraCardBalance] = useState(0);
    const [bonusCardBalance, setBonusCardBalance] = useState(0);
    const [unclaimedMilestones, setUnclaimedMilestones] = useState(0);
    const [hasPendingRequest, setHasPendingRequest] = useState(false);
    const [claimLoading, setClaimLoading] = useState(false);

    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('auth_token');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };
    }, []);

    const fetchCardStatus = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            if (!token) return;

            const response = await fetch(`${API_URL}/api/agent/captain-tc-cards`, {
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.cards) {
                    setCards(data.cards || []);
                }
                // Set promo wallet balances
                if (data.promo_wallet) {
                    setExtraCardBalance(data.promo_wallet.extra_card_balance || 0);
                    setBonusCardBalance(data.promo_wallet.bonus_card_balance || 0);
                    setUnclaimedMilestones(data.promo_wallet.unclaimed_milestones || 0);
                    setHasPendingRequest(data.promo_wallet.has_pending_request || false);
                }
            }
        } catch (err) {
            console.warn('Captain TC Cards Agent API not available:', err.message);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    const fetchSalesHistory = useCallback(async () => {
        try {
            setSalesLoading(true);
            const token = localStorage.getItem('auth_token');
            if (!token) return;

            const response = await fetch(`${API_URL}/api/agent/captain-tc-cards/sales-history`, {
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setSalesHistory(data.sales || []);
                    setAgentWalletBalance(data.agent_wallet_balance || 0);
                }
            }
        } catch (err) {
            console.warn('Sales history API not available:', err.message);
        } finally {
            setSalesLoading(false);
        }
    }, [getAuthHeaders]);

    const handleClaimCashback = async () => {
        if (hasPendingRequest || unclaimedMilestones <= 0) return;
        
        try {
            setClaimLoading(true);
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${API_URL}/api/agent/captain-tc-cards/claim-cashback`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });

            const data = await response.json();
            if (response.ok && data.success) {
                alert(data.message || 'Cash back claim submitted successfully.');
                setHasPendingRequest(true);
                // Reduce unclaimed visually immediately to avoid double clicks
                setUnclaimedMilestones(0); 
            } else {
                alert(data.message || 'Failed to submit claim.');
            }
        } catch (err) {
            console.error('Error claiming cash back:', err);
            alert('An error occurred during submission.');
        } finally {
            setClaimLoading(false);
        }
    };

    useEffect(() => {
        fetchCardStatus();
        fetchSalesHistory();
    }, [fetchCardStatus, fetchSalesHistory]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        } catch {
            return dateString;
        }
    };

    if (loading) {
        return (
            <div className="captain-tc-agent-wrapper">
                <div className="captain-tc-agent-loading">
                    <div className="captain-tc-agent-spinner"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="captain-tc-agent-wrapper">
            {/* Header */}
            <div className="captain-tc-agent-header">
                <div className="captain-tc-agent-header-left">
                    <span className="captain-tc-agent-label">Captain TC</span>
                    <h2 className="captain-tc-agent-title">Cash-Wallet Card</h2>
                </div>
            </div>

            {/* Card Tiers - Owned Status */}
            <div className="captain-tc-agent-cards-grid">
                {cards.map((card) => (
                    <div 
                        key={card.id || card.tier} 
                        className="captain-tc-agent-card-item"
                    >
                        {/* Title Header */}
                        <div style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold', fontSize: '18px', color: '#03173d' }}>
                            {card.name}
                        </div>

                        {/* Owned Header */}
                        <div className="captain-tc-agent-card-owned">
                            Owned: <span className={card.owned ? 'owned-yes' : 'owned-no'}>
                                {card.owned ? 'Yes' : 'No'}
                            </span>
                        </div>

                        {/* Visual Card */}
                        <div className="captain-tc-agent-card-body">
                            <img 
                                src={card.image ? (card.image.startsWith('http') ? card.image : `${API_URL}/storage/${card.image}`) : fivePoundCardImg} 
                                alt={`${card.name} Captain TC Card`} 
                                className="captain-tc-actual-card-img" 
                            />
                        </div>

                        {/* Serial Footer */}
                        <div className="captain-tc-agent-card-serial">
                            {card.owned && card.serial ? (
                                <span>Serial : <strong>{card.serial}</strong></span>
                            ) : (
                                <span>&nbsp;</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* New Promo Wallets Section */}
            <div className="captain-tc-agent-wallet-section" style={{ marginBottom: '32px' }}>
                <div className="captain-tc-agent-wallet-row">
                    <div className="captain-tc-agent-wallet-info">
                        <div className="captain-tc-agent-wallet-icon">
                            <img src={promoTopCoinIcon} alt="TopCoin Promo Wallet" style={{ backgroundColor: "white" }} />
                        </div>
                        <div>
                            <h4 className="captain-tc-agent-wallet-title">Captain TC Promotion TopCoin</h4>
                            <span className="captain-tc-agent-wallet-balance">
                                {extraCardBalance.toLocaleString()} TC
                            </span>
                        </div>
                    </div>
                </div>

                {/* <div className="captain-tc-agent-wallet-row">
                    <div className="captain-tc-agent-wallet-info">
                        <div className="captain-tc-agent-wallet-icon">
                            <img src={promoCashIcon} alt="Cash Promo Wallet" style={{ backgroundColor: "white" }} />
                        </div>
                        <div>
                            <h4 className="captain-tc-agent-wallet-title">Captain TC Promotional Cash Wallet</h4>
                            <span className="captain-tc-agent-wallet-balance">
                                £ {(bonusCardBalance * 0.005).toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div> */}
            </div>

            {/* Claim Cash Back Section */}
            {(unclaimedMilestones > 0 || hasPendingRequest) && (
                <div className="captain-tc-agent-claim-section" style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '32px' }}>
                    <h3 style={{ marginBottom: '10px', color: '#333' }}>Claim Cash Back</h3>
                    <p style={{ fontSize: '14px', color: '#555', marginBottom: '15px' }}>
                        Rules: You need at least 250 TopCoins in extra card balance to claim. Each card covers 20 claims. Once requested, the Account Admin will review and approve your bonus card balance.
                    </p>
                    <button 
                        onClick={handleClaimCashback}
                        disabled={claimLoading || hasPendingRequest}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: hasPendingRequest ? '#ccc' : '#001964',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: hasPendingRequest ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold'
                        }}
                    >
                        {claimLoading ? 'Submitting...' : hasPendingRequest ? 'Cash Back Requested (Pending Approval)' : `Claim Cash Back (${unclaimedMilestones} available)`}
                    </button>
                </div>
            )}

            {/* Sales History Section */}
            <div className="captain-tc-agent-sales-section">
                <h3 className="captain-tc-agent-sales-title">
                    The TopCoin Reward Promotion
                    <span className="captain-tc-agent-sales-subtitle">
                        (As the Agent Sells the Cards to the Customer)
                    </span>
                </h3>

                <div className="captain-tc-agent-table-container">
                    <table className="captain-tc-agent-table">
                        <thead>
                            <tr>
                                <th>No</th>
                                <th>Serial</th>
                                <th>Price</th>
                                <th>Date</th>
                                <th>TopCoin Earned</th>
                                <th>History</th>
                            </tr>
                        </thead>
                        <tbody>
                            {salesHistory.length > 0 ? (
                                salesHistory.map((sale, index) => (
                                    <tr key={sale.id || index}>
                                        <td>{index + 1}</td>
                                        <td className="serial-cell">{sale.serial || '—'}</td>
                                        <td>{sale.price || '—'}</td>
                                        <td>{formatDate(sale.date)}</td>
                                        <td className="topcoin-cell">
                                            {sale.topcoin_earned
                                                ? sale.topcoin_earned.toLocaleString()
                                                : '—'}
                                        </td>
                                        <td>
                                            <button
                                                className="captain-tc-agent-history-btn"
                                                disabled={!sale.serial}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <>
                                    {[1, 2, 3, 4].map((n) => (
                                        <tr key={n} className="empty-row">
                                            <td>{n}</td>
                                            <td>—</td>
                                            <td>—</td>
                                            <td>—</td>
                                            <td>—</td>
                                            <td>—</td>
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Agent TopCoin Wallet */}
            <div className="captain-tc-agent-wallet-section">
                <div className="captain-tc-agent-wallet-row">
                    <div className="captain-tc-agent-wallet-info">
                        <div className="captain-tc-agent-wallet-icon">
                            <img src={wallet2} alt="Wallet" style={{ backgroundColor: "white" }} />
                        </div>
                        <div>
                            <h4 className="captain-tc-agent-wallet-title">Advertising Agent TopCoin Wallet</h4>
                            <span className="captain-tc-agent-wallet-balance">
                                {agentWalletBalance.toLocaleString()} TC
                            </span>
                        </div>
                    </div>
                </div>

                <div className="captain-tc-agent-withdraw-row">
                    <div className="captain-tc-agent-withdraw-info">
                        <div className="captain-tc-agent-withdraw-icon">
                            <img src={oldbank} alt="Bank" style={{ backgroundColor: "white" }} />
                        </div>
                        <h4 className="captain-tc-agent-withdraw-title">Withdrawal</h4>
                    </div>
                    <button className="captain-tc-agent-withdraw-btn" disabled>
                        Withdraw
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CaptainTCCardAgent;
